import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import { DCard, BtnGold, BtnGhost, Badge, Avatar } from '../components/MarketplaceAtoms.js';
import PartnerRoleBadges from '../components/PartnerRoleBadges.js';
import { startListingCheckout } from '../../lib/marketplaceStripeService.js';
import { placeAuctionBid, auctionBuyNow, closeAuctionIfEnded, getAuctionBidSummaries } from '../../lib/auctionsService.js';
import { fetchVerifierStoresForCheckout } from '../../lib/verifierStoresService.js';
import {
  fetchPickupPointsForCheckout,
  formatPartnerPlaceLabel,
} from '../../lib/pickupPointsService.js';
import { fetchSellerPublicStats } from '../../lib/sellerPublicStatsService.js';
import SurcosRating from '../components/SurcosRating.js';
import PartnerVerifiedBadge from '../components/PartnerVerifiedBadge.js';
import ListingPhotoGallery from '../components/ListingPhotoGallery.js';
import { isDreamvinylVerifiedPartner } from '../utils/partnerVerified.js';
import { listingGalleryUrls } from '../utils/listingGalleryUrls.js';
import { recordListingView } from '../../lib/listingsService.js';
import BuyerPartnerDeliveryFields from '../components/BuyerPartnerDeliveryFields.js';
import {
  emptyBuyerShippingAddress,
  isBuyerShippingAddressComplete,
  buyerShippingAddressSnapshot,
} from '../constants/transactionLogistics.js';
import {
  isAuctionPendingVerification,
  isAuctionLive,
  auctionStatusLabel,
} from '../constants/auctionStatus.js';
import { buildPublicContentUrl } from '../../lib/socialPublishKit.js';

function formatListingEuro(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '0 €';
  return Number.isInteger(n) ? `${n} €` : `${n.toFixed(2)} €`;
}

const CONDITION_HINTS = {
  M: 'Como nuevo. Nunca usado.',
  'VG+': 'Casi perfecto, uso mínimo, sin ruido notable.',
  VG: 'Buen estado; alguna marca y leve crepitar en silencio.',
  'G+': 'Escuchable con defectos leves.',
  G: 'Escuchable con marcas y algo de ruido.',
  P: 'Muy marcado; valor sobre todo coleccionable.',
};

const DELIVERY_OPTION_HINTS = {
  direct: 'El vendedor envía por mensajería (plazo orientativo 2–5 días).',
  pickup: 'Recoges el disco en un punto DreamVinyl elegido al comprar.',
  verified: 'Un partner DreamVinyl revisa el disco antes de la entrega (máxima garantía).',
  physical: 'Quedada en persona con el vendedor; sin gastos de gestión en el precio.',
};

const FIELD_HINT_STYLE = {
  fontSize: 9.5,
  color: 'rgba(255,255,255,0.38)',
  lineHeight: 1.45,
  marginTop: 5,
};

function PartnerPlacePicker({ stores, loading, selectedId, onSelect, title, emptyHint }) {
  if (loading) {
    return (
      <div style={{ ...FIELD_HINT_STYLE, marginTop: 10, color: 'rgba(255,255,255,0.45)' }}>
        Cargando tiendas verificadoras…
      </div>
    );
  }
  if (!stores?.length) {
    return (
      <div style={{ ...FIELD_HINT_STYLE, marginTop: 10, color: C.warn }}>
        {emptyHint || 'No hay opciones disponibles ahora. Prueba otra forma de entrega.'}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 6, fontWeight: 600 }}>
        {title || 'Elige el partner'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
        {stores.map(s => {
          const id = s.partner_id;
          const sel = selectedId === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onSelect(id)}
              style={{
                textAlign: 'left',
                padding: '10px 12px',
                borderRadius: 10,
                border: `0.5px solid ${sel ? 'rgba(200,168,75,0.55)' : 'rgba(255,255,255,0.12)'}`,
                background: sel ? 'rgba(200,168,75,0.1)' : 'rgba(0,0,0,0.2)',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: sel ? C.gold : '#fff' }}>
                {formatPartnerPlaceLabel(s)}
              </div>
              {s.address ? (
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.42)', marginTop: 3, lineHeight: 1.4 }}>
                  {s.address}
                </div>
              ) : null}
              {s.code ? (
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.32)', marginTop: 2 }}>Código {s.code}</div>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function PageFichaDisc({
  item,
  onBack,
  onNavigate,
  onChatSeller,
  user,
  liked = false,
  onToggleLike,
  onRequireAuth,
  paymentsEnabled = true,
  auctionsEnabled = true,
  hiddenNavKeys = [],
}) {
  const [buyOpen, setBuyOpen] = useState(false);
  const [bidOpen, setBidOpen] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [auctionLive, setAuctionLive] = useState({ currentBid: null, bidCount: 0 });
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [verifierStores, setVerifierStores] = useState([]);
  const [verifierStoresLoading, setVerifierStoresLoading] = useState(false);
  const [selectedVerifierPartnerId, setSelectedVerifierPartnerId] = useState(null);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [pickupPointsLoading, setPickupPointsLoading] = useState(false);
  const [selectedPickupPartnerId, setSelectedPickupPartnerId] = useState(null);
  const [buyerDeliveryMode, setBuyerDeliveryMode] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(() => emptyBuyerShippingAddress());
  const [optSel, setOptSel] = useState(3);
  const basePrice = useMemo(() => {
    const n = Number(item?.isAuction ? item?.currentBid ?? item?.price : item?.price);
    return Number.isFinite(n) ? n : 0;
  }, [item?.isAuction, item?.currentBid, item?.price]);
  const shippingExtras = useMemo(() => [5, 3, 8, 0], []);
  const availableShipIdxs = useMemo(() => {
    const idxs = [];
    if (item?.shipsDirect) idxs.push(0);
    if (item?.shipsPickup) idxs.push(1);
    if (item?.shipsVerified) idxs.push(2);
    if (item?.shipsPhysical) idxs.push(3);
    return idxs;
  }, [item?.shipsDirect, item?.shipsPickup, item?.shipsVerified, item?.shipsPhysical]);
  const isShipIdxAvailable = useMemo(() => {
    const set = new Set(availableShipIdxs);
    return i => set.has(i);
  }, [availableShipIdxs]);
  /** L1 sin pagos: solo entrega entre usuarios (envío directo / quedada), no checkout partner ni Stripe. */
  const isShipIdxSelectable = useMemo(() => {
    const set = new Set(availableShipIdxs);
    return i => {
      if (!set.has(i)) return false;
      if (!paymentsEnabled && (i === 1 || i === 2)) return false;
      return true;
    };
  }, [availableShipIdxs, paymentsEnabled]);
  const selectableShipIdxs = useMemo(
    () => [0, 1, 2, 3].filter(i => isShipIdxSelectable(i)),
    [isShipIdxSelectable]
  );
  useEffect(() => {
    if (!selectableShipIdxs.length) return;
    if (selectableShipIdxs.includes(optSel)) return;
    setOptSel(selectableShipIdxs[0]);
  }, [selectableShipIdxs, optSel]);
  const selectedExtra = shippingExtras[optSel] ?? 0;
  const selectedTotalPrice = basePrice + selectedExtra;
  const optPrices = useMemo(
    () => shippingExtras.map(extra => basePrice + extra),
    [basePrice, shippingExtras]
  );
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const [sellerStats, setSellerStats] = useState({
    ventas: null,
    ratingAvg: null,
    ratingCount: 0,
    loading: false,
  });

  const galleryUrls = useMemo(() => listingGalleryUrls(item), [item]);

  useEffect(() => {
    if (!item?.id || String(item?.status || 'active').toLowerCase() !== 'active') return;
    recordListingView(item.id, {
      sellerUserId: item.sellerUserId,
      viewerUserId: user?.id,
    });
  }, [item?.id, item?.status, item?.sellerUserId, user?.id]);

  useEffect(() => {
    const sid = item?.sellerUserId;
    if (!sid) {
      setSellerStats({ ventas: null, ratingAvg: null, ratingCount: 0, loading: false });
      return;
    }
    let cancelled = false;
    setSellerStats(s => ({ ...s, loading: true }));
    (async () => {
      const stats = await fetchSellerPublicStats(sid);
      if (!cancelled) {
        setSellerStats({
          ventas: stats.ventas,
          ratingAvg: stats.ratingAvg,
          ratingCount: stats.ratingCount,
          loading: false,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item?.sellerUserId]);

  useEffect(() => {
    if (!item?.isAuction || !item?.id) return;
    let cancelled = false;
    (async () => {
      await closeAuctionIfEnded(item.id);
      const { summaries } = await getAuctionBidSummaries([item.id]);
      if (cancelled) return;
      const s = summaries[item.id];
      const start = item.auctionStart != null ? Number(item.auctionStart) : Number(item.price);
      setAuctionLive({
        currentBid: s?.currentBid != null ? s.currentBid : start,
        bidCount: s?.bidCount ?? 0,
      });
      const min = (s?.currentBid != null ? s.currentBid : start) + 1;
      setBidAmount(String(min));
    })();
    return () => {
      cancelled = true;
    };
  }, [item?.id, item?.isAuction, item?.auctionStart, item?.price]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const isSellerOwner = !!(user?.id && item?.sellerUserId && user.id === item.sellerUserId);
  const isReserved = String(item?.status || '').toLowerCase() === 'reserved';
  const isAuctionPending = !!item?.isAuction && isAuctionPendingVerification(item?.status);
  const canBidAuction = !!item?.isAuction && isAuctionLive(item?.status) && !isReserved;

  const sellerSurcosValue = useMemo(() => {
    if (sellerStats.ratingCount === 0 || sellerStats.ratingAvg == null) return 0;
    return Math.min(5, Math.max(1, Math.round(sellerStats.ratingAvg)));
  }, [sellerStats.ratingAvg, sellerStats.ratingCount]);

  const sellerLocationLine = useMemo(
    () => [item?.ciudad, item?.localidad].filter(Boolean).join(', ') || item?.location || '',
    [item?.ciudad, item?.localidad, item?.location]
  );

  const socialListeningUrl = useMemo(() => {
    const u = String(item?.audioLink || '').trim();
    return u && /^https?:\/\//i.test(u) ? u : null;
  }, [item?.audioLink]);

  const sellerIsPartnerVerified = useMemo(
    () => isDreamvinylVerifiedPartner(item),
    [item]
  );

  const conditionMeaning = useMemo(() => {
    const key = String(item?.condition || '').toUpperCase();
    const map = {
      M: 'Mint',
      'VG+': 'Very Good Plus',
      VG: 'Very Good',
      'G+': 'Good Plus',
      G: 'Good',
      P: 'Poor',
    };
    return map[key] || null;
  }, [item?.condition]);

  const shippingOptions = useMemo(() => {
    const opts = [];
    if (item?.shipsDirect) opts.push({ idx: 0, key: 'direct', icon: 'arrow_r', label: 'Envío directo' });
    if (item?.shipsPickup) opts.push({ idx: 1, key: 'pickup', icon: 'map', label: 'Punto de recogida' });
    if (item?.shipsVerified) opts.push({ idx: 2, key: 'verified', icon: 'store', label: 'Partner verificador' });
    if (item?.shipsPhysical) opts.push({ idx: 3, key: 'physical', icon: 'user', label: 'Entrega física' });
    return opts;
  }, [item?.shipsDirect, item?.shipsPickup, item?.shipsVerified, item?.shipsPhysical]);

  const conditionHint = useMemo(() => {
    const key = String(item?.condition || '').toUpperCase();
    return CONDITION_HINTS[key] || null;
  }, [item?.condition]);

  const selectedDeliveryHint = useMemo(() => {
    const opt = shippingOptions.find(o => o.idx === optSel);
    return opt ? DELIVERY_OPTION_HINTS[opt.key] || null : null;
  }, [shippingOptions, optSel]);

  const needsVerifierStore = optSel === 2 && item?.shipsVerified;
  const needsPickupPoint = optSel === 1 && item?.shipsPickup;
  const isPartnerHubCheckout = optSel === 1 || optSel === 2;

  useEffect(() => {
    setBuyerDeliveryMode(null);
    setShippingAddress(emptyBuyerShippingAddress());
  }, [optSel]);

  useEffect(() => {
    setSelectedVerifierPartnerId(null);
    setSelectedPickupPartnerId(null);
  }, [buyerDeliveryMode]);

  useEffect(() => {
    if (!item?.shipsVerified || !item?.id) {
      setVerifierStores([]);
      setSelectedVerifierPartnerId(null);
      return;
    }
    if (isPartnerHubCheckout && !buyerDeliveryMode) {
      setVerifierStores([]);
      setSelectedVerifierPartnerId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setVerifierStoresLoading(true);
      const { stores } = await fetchVerifierStoresForCheckout(item.id, buyerDeliveryMode || null);
      if (cancelled) return;
      setVerifierStores(stores);
      setVerifierStoresLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [item?.shipsVerified, item?.id, buyerDeliveryMode, isPartnerHubCheckout]);

  useEffect(() => {
    if (!item?.shipsPickup || !item?.id) {
      setPickupPoints([]);
      setSelectedPickupPartnerId(null);
      return;
    }
    if (isPartnerHubCheckout && !buyerDeliveryMode) {
      setPickupPoints([]);
      setSelectedPickupPartnerId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setPickupPointsLoading(true);
      const { points } = await fetchPickupPointsForCheckout(item.id, buyerDeliveryMode || null);
      if (cancelled) return;
      setPickupPoints(points);
      setPickupPointsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [item?.shipsPickup, item?.id, buyerDeliveryMode, isPartnerHubCheckout]);

  const checkoutPartnerBlocked = useMemo(() => {
    if (optSel === 1) {
      if (!buyerDeliveryMode) return true;
      if (!selectedPickupPartnerId || !pickupPoints.length) return true;
      if (buyerDeliveryMode === 'ship_to_buyer' && !isBuyerShippingAddressComplete(shippingAddress)) return true;
    }
    if (optSel === 2) {
      if (!buyerDeliveryMode) return true;
      if (!selectedVerifierPartnerId || !verifierStores.length) return true;
      if (buyerDeliveryMode === 'ship_to_buyer' && !isBuyerShippingAddressComplete(shippingAddress)) return true;
    }
    return false;
  }, [
    optSel,
    buyerDeliveryMode,
    shippingAddress,
    selectedPickupPartnerId,
    pickupPoints.length,
    selectedVerifierPartnerId,
    verifierStores.length,
  ]);

  useEffect(() => {
    if (optSel !== 2) return;
    if (selectedVerifierPartnerId) return;
    if (verifierStores.length === 1) {
      setSelectedVerifierPartnerId(verifierStores[0].partner_id);
    }
  }, [optSel, verifierStores, selectedVerifierPartnerId]);

  useEffect(() => {
    if (optSel !== 1) return;
    if (selectedPickupPartnerId) return;
    if (pickupPoints.length === 1) {
      setSelectedPickupPartnerId(pickupPoints[0].partner_id);
    }
  }, [optSel, pickupPoints, selectedPickupPartnerId]);

  const showToast = msg => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 2200);
  };

  const minNextBid = useMemo(() => {
    const cur = Number(auctionLive.currentBid ?? item?.auctionStart ?? item?.price ?? 0);
    return Number.isFinite(cur) ? cur + 1 : 1;
  }, [auctionLive.currentBid, item?.auctionStart, item?.price]);

  const openSellerChatForPurchase = useCallback(() => {
    if (!onChatSeller) {
      showToast('No se pudo abrir el chat con el vendedor.');
      return;
    }
    onChatSeller({
      sellerUsername: item.seller,
      sellerUserId: item.sellerUserId,
      listingId: item.id,
      listingTitle: item.title,
      listingPrice: item.price,
      listingCoverUrl: item.coverUrl ?? item.cover_url ?? null,
      listingArtist: item.artist ?? null,
      listingCondition: item.condition ?? null,
      sellerAvatarUrl: item.sellerAvatar ?? null,
      isAuction: false,
    });
  }, [item, onChatSeller]);

  const openBuyOrBid = () => {
    if (!user?.id) {
      onRequireAuth?.();
      return;
    }
    if (item?.isAuction) {
      if (!auctionsEnabled) {
        showToast('Las subastas no están disponibles en esta fase del lanzamiento.');
        return;
      }
      if (isAuctionPending) {
        showToast('La subasta aún no está publicada: el partner debe verificar el disco.');
        return;
      }
      if (!canBidAuction) {
        showToast('Esta subasta no está activa.');
        return;
      }
      setBidOpen(true);
      return;
    }
    if (!selectableShipIdxs.length) {
      showToast('Este anuncio no tiene una forma de compra disponible en esta fase.');
      return;
    }
    const preferred = !paymentsEnabled
      ? [3, 0].find(i => selectableShipIdxs.includes(i)) ?? selectableShipIdxs[0]
      : selectableShipIdxs[0];
    setOptSel(preferred);
    setBuyOpen(true);
  };

  const confirmPurchase = async () => {
    if (!user?.id) {
      onRequireAuth?.();
      return;
    }
    if (!paymentsEnabled) {
      if (!isShipIdxSelectable(optSel)) {
        showToast('Elige cómo quieres recibir el disco (entre vosotros, sin pago en la app).');
        return;
      }
      setBuyOpen(false);
      openSellerChatForPurchase();
      return;
    }
    if (optSel === 3) {
      setBuyOpen(false);
      openSellerChatForPurchase();
      return;
    }
    if (optSel === 2 || optSel === 1) {
      if (!buyerDeliveryMode) {
        showToast('Elige cómo quieres recibir el disco desde el partner.');
        return;
      }
      if (buyerDeliveryMode === 'ship_to_buyer' && !isBuyerShippingAddressComplete(shippingAddress)) {
        showToast('Completa la dirección de envío.');
        return;
      }
    }
    if (optSel === 2) {
      if (!verifierStores.length) {
        showToast('No hay tiendas para esa modalidad. Prueba recogida en local u otra entrega.');
        return;
      }
      if (!selectedVerifierPartnerId) {
        showToast('Elige la tienda verificadora donde se entregará el disco.');
        return;
      }
    }
    if (optSel === 1) {
      if (!pickupPoints.length) {
        showToast('No hay puntos para esa modalidad. Prueba recogida en local u otra entrega.');
        return;
      }
      if (!selectedPickupPartnerId) {
        showToast('Elige el punto de recogida.');
        return;
      }
    }
    setCheckoutBusy(true);
    const { ok, error } = await startListingCheckout({
      listingId: item.id,
      shippingOption: optSel,
      verifierPartnerId: optSel === 2 ? selectedVerifierPartnerId : null,
      pickupPartnerId: optSel === 1 ? selectedPickupPartnerId : null,
      buyerPartnerDeliveryMode: isPartnerHubCheckout ? buyerDeliveryMode : null,
      buyerShippingAddress:
        isPartnerHubCheckout && buyerDeliveryMode === 'ship_to_buyer'
          ? buyerShippingAddressSnapshot(shippingAddress)
          : null,
    });
    if (!ok) {
      setCheckoutBusy(false);
      showToast(error || 'No se pudo iniciar el pago.');
      return;
    }
    setBuyOpen(false);
    showToast('Redirigiendo a Stripe…');
  };

  const submitBid = async () => {
    if (!user?.id) {
      onRequireAuth?.();
      return;
    }
    const amount = Number(String(bidAmount).replace(',', '.'));
    if (!Number.isFinite(amount) || amount < minNextBid) {
      showToast(`Puja mínima: ${minNextBid} €`);
      return;
    }
    setCheckoutBusy(true);
    const { error } = await placeAuctionBid(item.id, amount);
    setCheckoutBusy(false);
    if (error) {
      showToast(error);
      return;
    }
    setBidOpen(false);
    showToast('Puja registrada');
    const { summaries } = await getAuctionBidSummaries([item.id]);
    const s = summaries[item.id];
    setAuctionLive({
      currentBid: s?.currentBid ?? amount,
      bidCount: (s?.bidCount ?? auctionLive.bidCount) + 1,
    });
    setBidAmount(String((s?.currentBid ?? amount) + 1));
  };

  const submitBuyNow = async () => {
    if (!item?.auctionBuyNow) return;
    if (!user?.id) {
      onRequireAuth?.();
      return;
    }
    setCheckoutBusy(true);
    const { error } = await auctionBuyNow(item.id);
    setCheckoutBusy(false);
    if (error) {
      showToast(error);
      return;
    }
    setBidOpen(false);
    showToast('¡Compra inmediata registrada!');
    if (onNavigate) onNavigate('pedido');
  };

  const shareListing = async () => {
    const url = buildPublicContentUrl('listing', item?.id ?? '');
    const title = item?.title ? `DreamVinyl · ${item.title}` : 'DreamVinyl';
    try {
      if (navigator.share) {
        await navigator.share({ title, text: item?.artist ? `${item.artist} · ${item.title}` : title, url });
        showToast('Compartido');
        return;
      }
    } catch (_) {
      // si el usuario cancela, no hacemos nada
      showToast('No se pudo compartir');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast('Enlace copiado');
    } catch (_) {
      showToast('Copia manual: ' + url);
    }
  };

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
      <WebTopBar
        onBack={onBack}
        title={item.title}
        mini
        dark
        user={user}
        onNavigate={onNavigate}
        onSignOut={onBack}
        hiddenNavKeys={hiddenNavKeys}
      />

      <div style={{ background: '#1a0d2e', position: 'relative', borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '22px 20px 18px', position: 'relative' }}>
          <div style={{ display: 'flex', gap: 22, alignItems: 'stretch', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 340px', maxWidth: '100%' }}>
              <ListingPhotoGallery
                urls={galleryUrls}
                alt={`${item.title || 'Anuncio'} — ${item.artist || ''}`}
                fallbackItem={item}
              />
            </div>

            <div style={{ flex: '1 1 420px', minWidth: 0, paddingTop: 6 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h1 style={{ fontSize: 24, fontWeight: 650, color: '#fff', marginBottom: 5, lineHeight: 1.2 }}>
                    {item.title}
                  </h1>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: -2 }}>
                      <button
                        type="button"
                        onClick={onToggleLike}
                        title={liked ? 'Quitar de Deseos' : 'Añadir a Deseos'}
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          background: 'rgba(0,0,0,0.35)',
                          border: '0.5px solid rgba(255,255,255,0.14)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        <Icon name="heart" size={14} color={liked ? '#ff4d6d' : '#fff'}/>
                      </button>
                      <button
                        type="button"
                        onClick={shareListing}
                        title="Compartir"
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          background: 'rgba(0,0,0,0.35)',
                          border: '0.5px solid rgba(255,255,255,0.14)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        <Icon name="share" size={14} color="#fff"/>
                      </button>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                    {item.artist} · {item.year} · {item.genre}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                  {sellerIsPartnerVerified ? <PartnerVerifiedBadge size={20} /> : null}
                  {isReserved ? <Badge type="warn">Reservado</Badge> : null}
                  {isAuctionPending ? <Badge type="gray">{auctionStatusLabel(item.status)}</Badge> : null}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                {[
                  ['Formato', item.type || '—', null],
                  [
                    'Estado',
                    item.condition ? `${item.condition}${conditionMeaning ? ` (${conditionMeaning})` : ''}` : '—',
                    conditionHint,
                  ],
                  ['Año', item.year ?? '—', null],
                  ['País', item.pais || '—', null],
                  [
                    'Ciudad',
                    item.ciudad
                      ? [item.ciudad, item.localidad].filter(Boolean).join(', ')
                      : item.location || '—',
                    null,
                  ],
                  ['Género', item.genre || '—', null],
                  ['Sello', item.label || '—', null],
                ].map(([l, v, hint]) => (
                  <DCard key={l} style={{ padding: '8px 10px', borderColor: 'rgba(255,255,255,0.12)' }}>
                    <div style={{ fontSize: 8.5, color: 'rgba(255,255,255,0.4)', marginBottom: 2 }}>{l}</div>
                    <div
                      style={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: '#fff',
                        whiteSpace: hint ? 'normal' : 'nowrap',
                        overflow: hint ? 'visible' : 'hidden',
                        textOverflow: hint ? 'clip' : 'ellipsis',
                        lineHeight: 1.3,
                      }}
                    >
                      {v}
                    </div>
                    {hint ? <div style={FIELD_HINT_STYLE}>{hint}</div> : null}
                  </DCard>
                ))}
              </div>

              <DCard style={{ padding: '10px 12px', marginBottom: 12, borderColor: 'rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)' }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>Tipos de entrega</div>
                <div style={{ ...FIELD_HINT_STYLE, marginTop: 0, marginBottom: 8 }}>
                  Formas de recibir el disco que ofrece este anuncio. Elige una antes de comprar; si tiene coste, se suma
                  como gastos de gestión al precio.
                </div>
                {shippingOptions.length ? (
                  <>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {shippingOptions.map(opt => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setOptSel(opt.idx)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 10px',
                            borderRadius: 999,
                            border: `0.5px solid ${opt.idx === optSel ? 'rgba(200,168,75,0.6)' : 'rgba(255,255,255,0.16)'}`,
                            background: opt.idx === optSel ? 'rgba(200,168,75,0.12)' : 'rgba(0,0,0,0.15)',
                            fontSize: 11,
                            color: 'rgba(255,255,255,0.9)',
                            cursor: 'pointer',
                          }}
                        >
                          <Icon name={opt.icon} size={13} color={C.gold} /> {opt.label}
                        </button>
                      ))}
                    </div>
                    {selectedDeliveryHint ? (
                      <div style={{ ...FIELD_HINT_STYLE, marginTop: 8, color: 'rgba(200,168,75,0.55)' }}>
                        {selectedDeliveryHint}
                      </div>
                    ) : null}
                    {needsPickupPoint ? (
                      <>
                        <BuyerPartnerDeliveryFields
                          buyerDeliveryMode={buyerDeliveryMode}
                          onBuyerDeliveryModeChange={setBuyerDeliveryMode}
                          shippingAddress={shippingAddress}
                          onShippingAddressChange={setShippingAddress}
                        />
                        {buyerDeliveryMode ? (
                          <PartnerPlacePicker
                            stores={pickupPoints}
                            loading={pickupPointsLoading}
                            selectedId={selectedPickupPartnerId}
                            onSelect={setSelectedPickupPartnerId}
                            title="Elige el punto donde el vendedor dejará el disco"
                            emptyHint="No hay puntos para esta modalidad. Prueba recoger en el local."
                          />
                        ) : null}
                      </>
                    ) : null}
                    {needsVerifierStore ? (
                      <>
                        <BuyerPartnerDeliveryFields
                          buyerDeliveryMode={buyerDeliveryMode}
                          onBuyerDeliveryModeChange={setBuyerDeliveryMode}
                          shippingAddress={shippingAddress}
                          onShippingAddressChange={setShippingAddress}
                        />
                        {buyerDeliveryMode ? (
                          <PartnerPlacePicker
                            stores={verifierStores}
                            loading={verifierStoresLoading}
                            selectedId={selectedVerifierPartnerId}
                            onSelect={setSelectedVerifierPartnerId}
                            title="Elige la tienda donde el vendedor entregará el disco"
                            emptyHint="No hay tiendas para esta modalidad. Prueba recoger en el local."
                          />
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                    Consultar al vendedor qué opciones de entrega ofrece.
                  </div>
                )}
              </DCard>

              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                <div style={{ minWidth: 0 }} role="group" aria-label="Desglose de precio">
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      gap: '4px 10px',
                      fontSize: 12,
                      color: 'rgba(255,255,255,0.5)',
                      marginBottom: selectedExtra > 0 ? 4 : 6,
                    }}
                  >
                    <span>Precio</span>
                    <span style={{ fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.88)' }}>
                      {formatListingEuro(basePrice)}
                    </span>
                  </div>
                  {selectedExtra > 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'baseline',
                        gap: '4px 10px',
                        fontSize: 12,
                        color: 'rgba(255,255,255,0.5)',
                        marginBottom: 6,
                      }}
                      title="Según el tipo de entrega elegido: envío, partner verificador, recogida, etc."
                    >
                      <span>Gastos de gestión</span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>
                        +{formatListingEuro(selectedExtra)}
                      </span>
                    </div>
                  ) : null}
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      gap: '4px 10px',
                      paddingTop: selectedExtra > 0 ? 6 : 0,
                      borderTop: selectedExtra > 0 ? '0.5px solid rgba(255,255,255,0.12)' : 'none',
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.45)' }}>Total</span>
                    <span style={{ fontSize: 28, fontWeight: 650, color: C.gold, lineHeight: 1.1 }}>
                      {formatListingEuro(selectedTotalPrice)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={openBuyOrBid}
                  disabled={
                    (isReserved && !isSellerOwner) ||
                    isAuctionPending ||
                    (item.isAuction && !canBidAuction) ||
                    (item.isAuction && !auctionsEnabled) ||
                    (!item.isAuction && !selectableShipIdxs.length)
                  }
                  style={{
                    padding: '12px 16px',
                    borderRadius: 12,
                    background: C.gold,
                    color: '#0a0a0f',
                    border: 'none',
                    fontSize: 13,
                    fontWeight: 800,
                    cursor:
                      (isReserved && !isSellerOwner) ||
                      isAuctionPending ||
                      (item.isAuction && !canBidAuction) ||
                      (item.isAuction && !auctionsEnabled) ||
                      (!item.isAuction && !selectableShipIdxs.length)
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      (isReserved && !isSellerOwner) ||
                      isAuctionPending ||
                      (item.isAuction && !canBidAuction) ||
                      (item.isAuction && !auctionsEnabled) ||
                      (!item.isAuction && !selectableShipIdxs.length)
                        ? 0.45
                        : 1,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {item.isAuction
                    ? !auctionsEnabled
                      ? 'SUBASTAS — PRÓXIMAMENTE'
                      : isAuctionPending
                        ? 'PENDIENTE VERIFICACIÓN'
                        : 'HACER UNA PUJA'
                    : 'COMPRAR AHORA'}
                </button>
              </div>
              <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.12)', margin: '14px 0 0' }}/>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '14px 20px 18px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.85fr', gap: 12, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
            {item?.esEdicionEspecial ? (
              <DCard style={{ padding: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 6, fontWeight: 600 }}>
                  Edición especial
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    ['Nombre', item.edicionNombre || '—'],
                    ['Catálogo', item.edicionCatalogo || '—'],
                    ['Contenido', item.edicionContenido || '—'],
                    ['Numerada', item.edicionNumerada ? 'Sí' : 'No'],
                    item.edicionNumerada ? ['Ejemplar nº', item.edicionNumeroEjemplar ?? '—'] : null,
                  ]
                    .filter(Boolean)
                    .map(([l, v]) => (
                      <div key={l} style={{ border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '10px 12px', background: 'rgba(255,255,255,0.03)' }}>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginBottom: 3 }}>{l}</div>
                        <div style={{ fontSize: 12, color: '#fff', fontWeight: 600 }}>{v}</div>
                      </div>
                    ))}
                </div>
              </DCard>
            ) : null}

            <DCard style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 5, fontWeight: 500 }}>Observaciones del vendedor</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                {item.description?.trim() ? item.description.trim() : 'Sin descripción añadida por el vendedor.'}
              </div>
            </DCard>
          </div>

          <div style={{ minWidth: 0 }}>
            <DCard style={{ padding: '10px 12px', marginBottom: 10 }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
            onClick={() =>
              onNavigate &&
              onNavigate('perfil-vendedor', {
                userId: item.sellerUserId,
                username: item.seller,
              })
            }
          >
            <Avatar
              initials={(item.seller || '?').slice(0, 2).toUpperCase()}
              bg="#534AB7"
              color="#EEEDFE"
              size={36}
              imageUrl={item.sellerAvatar}
            />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>@{item.seller}</div>
                {sellerIsPartnerVerified ? <PartnerVerifiedBadge size={17} /> : null}
              </div>
              <PartnerRoleBadges roleKeys={item.sellerPartnerRoleKeys} theme="dark" style={{ marginTop: 6 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                {sellerStats.loading ? (
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>Cargando valoración…</span>
                ) : sellerStats.ratingCount > 0 && sellerStats.ratingAvg != null ? (
                  <SurcosRating value={sellerSurcosValue} readOnly size="sm" />
                ) : (
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>Sin valoraciones aún</span>
                )}
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                  {sellerStats.loading
                    ? '…'
                    : `${sellerStats.ventas ?? 0} venta${sellerStats.ventas === 1 ? '' : 's'}`}
                  {sellerLocationLine ? ` · ${sellerLocationLine}` : ''}
                </span>
              </div>
            </div>
            <span style={{ fontSize: 10, color: C.gold }}>Ver perfil →</span>
          </div>
          {socialListeningUrl ? (
            <div
              style={{
                marginBottom: 10,
                padding: '10px 12px',
                borderRadius: 8,
                border: '0.5px solid rgba(200,168,75,0.25)',
                background: 'rgba(200,168,75,0.06)',
                fontSize: 11,
                lineHeight: 1.45,
              }}
            >
              <div style={{ color: C.gold, fontWeight: 600, marginBottom: 4 }}>Escuchar en DreamVinyl</div>
              <a
                href={socialListeningUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'underline' }}
              >
                Abrir publicación en redes (muestra del disco)
              </a>
              <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.38)', marginTop: 4 }}>
                La escucha se realiza en nuestras redes oficiales, no en el reproductor de la web.
              </div>
            </div>
          ) : null}
          <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.06)', margin: '10px 0' }}/>
          <button
            type="button"
            disabled={!item.sellerUserId || (user?.id && user.id === item.sellerUserId)}
            onClick={() =>
              onChatSeller &&
              onChatSeller({
                sellerUsername: item.seller,
                sellerUserId: item.sellerUserId,
                listingId: item.id,
                listingTitle: item.title,
                listingPrice: item.price,
                sellerAvatarUrl: item.sellerAvatar ?? null,
                isAuction: !!item.isAuction,
              })
            }
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: 9,
              background: 'rgba(200,168,75,0.08)',
              border: `0.5px solid rgba(200,168,75,0.2)`,
              color: C.gold,
              fontSize: 11,
              cursor: !item.sellerUserId || (user?.id && user.id === item.sellerUserId) ? 'not-allowed' : 'pointer',
              opacity: !item.sellerUserId || (user?.id && user.id === item.sellerUserId) ? 0.45 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <Icon name="chat" size={13} color={C.gold} /> Enviar mensaje al vendedor
          </button>
        </DCard>
            {isReserved && !isSellerOwner ? (
              <DCard
                style={{
                  padding: '10px 12px',
                  borderColor: 'rgba(239,159,39,0.3)',
                  background: 'rgba(239,159,39,0.08)',
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 500, color: C.warn, marginBottom: 3 }}>Artículo reservado</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
                  Este anuncio está reservado temporalmente por el vendedor.
                </div>
              </DCard>
            ) : null}
          </div>
        </div>
      </div>

      {toast ? (
        <div style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 18, zIndex: 500 }}>
          <div style={{ background: 'rgba(0,0,0,0.75)', border: '0.5px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.92)', padding: '10px 14px', borderRadius: 999, fontSize: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.35)' }}>
            {toast}
          </div>
        </div>
      ) : null}

      {buyOpen && (
        <div onClick={e => { if (e.target === e.currentTarget) setBuyOpen(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: C.darkMid, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 640, margin: '0 auto', padding: 20, borderTop: '0.5px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: '#fff', marginBottom: 4 }}>
              {paymentsEnabled ? 'Elige cómo recibirlo' : 'Compra directa con el vendedor'}
            </div>
            {!paymentsEnabled ? (
              <div
                style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.55)',
                  marginBottom: 12,
                  lineHeight: 1.5,
                  padding: '8px 10px',
                  borderRadius: 10,
                  background: 'rgba(200,168,75,0.08)',
                  border: '0.5px solid rgba(200,168,75,0.2)',
                }}
              >
                En esta fase no hay pago en la app. Elige cómo os organizáis la entrega y acordad precio y pago por
                chat con el vendedor.
              </div>
            ) : null}
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 14, lineHeight: 1.55 }}>
              <div>
                Precio: <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{formatListingEuro(basePrice)}</strong>
              </div>
              {selectedExtra > 0 ? (
                <div>
                  Gastos de gestión:{' '}
                  <strong style={{ color: 'rgba(255,255,255,0.75)' }}>+{formatListingEuro(selectedExtra)}</strong>
                </div>
              ) : null}
              <div>
                Total: <strong style={{ color: C.gold }}>{formatListingEuro(selectedTotalPrice)}</strong>
              </div>
            </div>
            {[
              { icon: 'arrow_r', name: 'Envío directo',       sub: 'Correo · 2-5 días',                  extra: '+5 €' },
              { icon: 'map',     name: 'Punto de recogida',   sub: 'Elige un punto cercano',             extra: '+3 €' },
              { icon: 'store',   name: 'Partner verificador', sub: 'Elige tienda · verificación y entrega', extra: '+8 €' },
              { icon: 'user',    name: 'Entrega física',       sub: 'Quedada con el vendedor',            extra: 'Gratis' },
            ].map((opt, i) => (
              <div
                key={i}
                onClick={() => {
                  if (!isShipIdxAvailable(i)) return;
                  setOptSel(i);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: 12,
                  borderRadius: 12,
                  border: `0.5px solid ${optSel === i ? C.gold : 'rgba(255,255,255,0.1)'}`,
                  background: optSel === i ? 'rgba(200,168,75,0.07)' : 'rgba(255,255,255,0.03)',
                  marginBottom: 8,
                  cursor: isShipIdxAvailable(i) ? 'pointer' : 'not-allowed',
                  opacity: isShipIdxAvailable(i) ? 1 : 0.35,
                }}
              >
                <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={opt.icon} size={15} color={C.gold}/>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#fff' }}>{opt.name}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', marginTop: 1 }}>{opt.sub}</div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: i === 3 ? 'rgba(255,255,255,0.4)' : C.gold }}>{opt.extra}</span>
              </div>
            ))}
            {paymentsEnabled && buyOpen && optSel === 1 ? (
              <>
                <BuyerPartnerDeliveryFields
                  buyerDeliveryMode={buyerDeliveryMode}
                  onBuyerDeliveryModeChange={setBuyerDeliveryMode}
                  shippingAddress={shippingAddress}
                  onShippingAddressChange={setShippingAddress}
                />
                {buyerDeliveryMode ? (
                  <PartnerPlacePicker
                    stores={pickupPoints}
                    loading={pickupPointsLoading}
                    selectedId={selectedPickupPartnerId}
                    onSelect={setSelectedPickupPartnerId}
                    title="Elige el punto de recogida"
                    emptyHint="No hay puntos para esta modalidad."
                  />
                ) : null}
              </>
            ) : null}
            {paymentsEnabled && buyOpen && optSel === 2 ? (
              <>
                <BuyerPartnerDeliveryFields
                  buyerDeliveryMode={buyerDeliveryMode}
                  onBuyerDeliveryModeChange={setBuyerDeliveryMode}
                  shippingAddress={shippingAddress}
                  onShippingAddressChange={setShippingAddress}
                />
                {buyerDeliveryMode ? (
                  <PartnerPlacePicker
                    stores={verifierStores}
                    loading={verifierStoresLoading}
                    selectedId={selectedVerifierPartnerId}
                    onSelect={setSelectedVerifierPartnerId}
                    title="Elige la tienda verificadora"
                    emptyHint="No hay tiendas para esta modalidad."
                  />
                ) : null}
              </>
            ) : null}
            <BtnGold
              onClick={confirmPurchase}
              disabled={checkoutBusy || (paymentsEnabled && checkoutPartnerBlocked)}
              style={{ marginTop: 8, borderRadius: 12 }}
            >
              {checkoutBusy
                ? 'Procesando…'
                : !paymentsEnabled || optSel === 3
                  ? 'Abrir chat y acordar compra'
                  : `Pagar con Stripe — ${optPrices[optSel]} €`}
            </BtnGold>
            <BtnGhost onClick={() => setBuyOpen(false)} disabled={checkoutBusy} style={{ marginTop: 6 }}>
              Cancelar
            </BtnGhost>
          </div>
        </div>
      )}

      {bidOpen && item?.isAuction ? (
        <div
          onClick={e => {
            if (e.target === e.currentTarget) setBidOpen(false);
          }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'flex-end' }}
        >
          <div style={{ background: C.darkMid, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 640, margin: '0 auto', padding: 20, borderTop: '0.5px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: '#fff', marginBottom: 4 }}>Pujar en subasta</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>
              Puja actual: {auctionLive.currentBid ?? item.auctionStart ?? item.price} € · {auctionLive.bidCount} pujas
              {item.auctionEndsAt
                ? ` · Fin ${new Date(item.auctionEndsAt).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}`
                : ''}
            </div>
            <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Tu puja (€)</label>
            <input
              type="number"
              min={minNextBid}
              step="1"
              value={bidAmount}
              onChange={e => setBidAmount(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: 10,
                border: '0.5px solid rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                fontSize: 16,
                marginBottom: 12,
                boxSizing: 'border-box',
              }}
            />
            <BtnGold onClick={submitBid} disabled={checkoutBusy} style={{ borderRadius: 12 }}>
              {checkoutBusy ? 'Enviando…' : `Pujar (mín. ${minNextBid} €)`}
            </BtnGold>
            {item.auctionBuyNow ? (
              <BtnGhost onClick={submitBuyNow} disabled={checkoutBusy} style={{ marginTop: 8 }}>
                Comprar ya — {item.auctionBuyNow} €
              </BtnGhost>
            ) : null}
            <BtnGhost onClick={() => setBidOpen(false)} disabled={checkoutBusy} style={{ marginTop: 6 }}>
              Cancelar
            </BtnGhost>
          </div>
        </div>
      ) : null}
    </div>
  );
}
